<script lang="ts">
  import {metadata, user} from '@/store';
  import {Item} from '@/types/metadata';
  import {onDestroy, onMount} from 'svelte';
  import {toasts} from 'svelte-toasts';
  import {writable} from 'svelte/store';
  import Swal from 'sweetalert2';
  import editorStore from '../../../../lib/editor/editor.store';
  import googleFontsStore from '../../../../lib/editor/FontSelector/google-fonts.service';
  import {getInstance} from '../../../../services/animation';
  import {loadUserFont} from '../../../../services/animation/font-loader';
  import {assetStore, loadAssets} from '../../../../services/asset/asset.store';
  import type {UploadPayload} from '../../../../types/upload';
  import {createFileUploadDialog} from '../../../../utils/common';
  import {validateFileType} from '../../../../utils/file';
  import {httpClient} from '../../../../utils/http-client';
  import {logError} from '../../../../utils/logs';
  import {FONT_TYPES} from './FontSelection';
  import {tooltip} from '@/actions/tippy-action';
  import {doNotHidePopupOnNextClick, hidePopupOnNextClick} from '../../PopupBox/PopupBox.svelte';
  import {AssetService} from '@/services';

  export let targets: HTMLElement | null;
  export let originalFont: string = '';
  export let onFontFamilyChanged: any | undefined;
  export let targetSelector: string = '';

  let activeComponent: any = null;
  let page = 1;
  let showFavorites = false;
  let totalFonts = 0;
  let isUpdatingFavorites = false;

  const itemsPerPage = 40;
  const uploadProgress = writable({
    uploading: false,
    progressPercent: 0,
    name: ''
  });
  const unsubscribe = editorStore.subscribe(store => {
    if (store.activeComponent == activeComponent) return;
    activeComponent = store.activeComponent;
  });

  const assetService = new AssetService();
  function deleteItem(font: any) {
    fontAssets = fontAssets.filter(item => item.id !== font.id);
    assetService.deleteAsset(font.id).then(resp => {
      console.log('Font deleted', resp);
    });
  }

  function handleDelete(font: any) {
    setTimeout(() => {
      doNotHidePopupOnNextClick(true); // This will prevent the image popup sidebarr from closing when delete confirmation is clicked
    }, 250); // The timeout is required to debounce the click event which sometimes causes it to trigger a hide event
    // console.log('Delete image', item);
    Swal.fire({
      title: 'Are you sure you want to delete this font?',
      showCancelButton: true,
      didClose: () => {
        hidePopupOnNextClick(); // This reactivates the default functionality of the image popup sidebar on click (anywhere on the page it will close)
      }
    }).then(result => {
      if (result.isConfirmed) {
        deleteItem(font);
      }
    });
  }

  $: favoritesFonts = $user.preferences?.favorites?.fonts || [];

  $: fontAssets = $assetStore.assets
    .filter(asset => asset.category.name === 'font')
    .map(font => ({...font, label: font.name, value: font.name}));

  $: getGoogleFonts = (fontData: Array<Item> | undefined, _page: number) => {
    const filteredFonts = fontData?.filter(font => (showFavorites ? favoritesFonts.includes(font.label) : true)) || [];
    totalFonts = filteredFonts.length;

    return filteredFonts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  };

  $: isFirstPage = page == 1;
  $: isLastPage = totalFonts <= page * itemsPerPage;

  onMount(() => {
    if (!$googleFontsStore.fonts.length) {
      googleFontsStore.getFonts();
    }

    loadAssets();
  });

  onDestroy(unsubscribe);

  const upload = async (): Promise<UploadPayload[`font`] | null> => {
    try {
      const files = await createFileUploadDialog();
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (file) {
          if (validateFileType(file.name, FONT_TYPES)) {
            formData.append('files', file);
          } else {
            Swal.fire({
              title: 'Invalid file type',
              text: `Please upload a valid font file`,
              icon: 'error'
            });
            return null;
          }
        } else {
          if (file) formData.append('files', file);
        }
      }
      formData.append('type', `font`);
      uploadProgress.update(store => ({...store, uploading: true}));
      const response = await httpClient.post('/assets', formData, {
        onUploadProgress: progressEvent => {
          const {loaded, total = 0} = progressEvent;
          const progress = (loaded / total) * 100;
          uploadProgress.update(store => ({
            ...store,
            progressPercent: progress
          }));
        }
      });
      uploadProgress.set({
        uploading: false,
        progressPercent: 0,
        name: ''
      });
      const data = response.data;
      return data;
    } catch (err) {
      logError('Error in upload', err);
      Swal.fire({
        titleText: 'Problem with upload',
        text: 'There was a problem with your file. Our service could not successfully process it, if the issue continues to persist, please contact us',
        icon: 'error'
      });
    }
    return null;
  };

  const uploadFont = async () => {
    const data = await upload();
    if (data) {
      toasts.add({
        title: 'Success',
        description: 'Font file successfully uploaded',
        duration: 1500, // 0 or negative to avoid auto-remove
        placement: 'top-left',
        type: 'success',
        theme: 'light'
      });
    }
    loadAssets();
  };

  const getFirstScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) return null;
    const overflowY = window.getComputedStyle(element).overflowY;
    const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';
    if (isScrollable && element.scrollHeight > element.clientHeight) {
      return element;
    }
    return getFirstScrollableParent(element.parentElement);
  };

  const turnPage = (_page = 1, ev: MouseEvent) => {
    page += _page;
    const parentScrollable = getFirstScrollableParent(ev.target as HTMLElement);
    if (parentScrollable) parentScrollable.scrollTop = 0;
  };

  const computeFamily = (font: any) => {
    return `font-family: '${font.label}'`;
  };

  const loadCustomerUserFont = (node: any, font: any, retries = 0) => {
    // node.innerHTML = font.label;
    loadUserFont(font.label, font.file.publicUrl)
      .then(resp => {
        node.style.fontFamily = font.label;
      })
      .catch(err => {
        logError('[loadCustomerUserFont] Error loading font', err);
        if (retries < 3) {
          setTimeout(() => {
            loadCustomerUserFont(node, font, retries + 1);
          }, 1000);
        }
      });
  };

  const changeFont = (font: any, isUserFont = false) => {
    getInstance().setMetaData({fontFamily: null});
    const elements = document.querySelectorAll(targetSelector) as NodeListOf<HTMLElement>;
    elements.forEach(element => {
      element.style.fontFamily = `'${font.label}'`;
      element.removeAttribute('data-user-fonturl');
      element.removeAttribute('data-user-fontname');
      if (isUserFont) {
        element.setAttribute('data-user-fonturl', font.file.publicUrl);
        element.setAttribute('data-user-fontname', font.label);
      }
    });
    originalFont = font.value;
    if (onFontFamilyChanged) onFontFamilyChanged(font, isUserFont);
  };

  const toggleFavorite = async (font: string) => {
    const isFavorite = favoritesFonts.includes(font);
    isUpdatingFavorites = true;
    await user.updatePreferences({
      favorites: {
        fonts: isFavorite ? favoritesFonts.filter((f: string) => f !== font) : [...favoritesFonts, font]
      }
    });
    isUpdatingFavorites = false;
  };
</script>

<div class="h-full pb-20">
  <div class="personal-fonts mb-4 px-2">
    <div class="flex items-center">
      <h1 class="block text-2xl font-bold">Custom fonts</h1>
      <button
        class="my-2 ml-auto block rounded-lg border-2 border-primary p-1 px-2 text-xs font-bold text-primary transition-colors duration-100 hover:bg-primary-600 hover:text-white"
        on:click={uploadFont}><i class="fa fa-plus text-xs"></i> Upload</button
      >
    </div>
    <div class="flex flex-col gap-2 rounded-md border-gray-200 text-xs text-gray-500">
      {#if $assetStore.loading == true}
        Loading...
      {:else if $assetStore.assets.length == 0}
        You currently do not have any uploaded fonts.
      {:else}
        {#each fontAssets as font}
          <div
            use:loadCustomerUserFont={font}
            on:click={() => changeFont(font, true)}
            class="relative block flex cursor-pointer flex-row items-center rounded-md border border-gray-200 bg-white p-2 text-xl hover:bg-gray-100"
            style={`font-family: '${font.label}'`}
          >
            <span class="flex flex-grow">
              {font.label}
            </span>
            <button
              class="z-10 mx-2 flex"
              use:tooltip={'Delete'}
              on:click={() => handleDelete(font)}
              on:keypress={() => handleDelete(font)}
            >
              <i class="fa fa-trash text-xl text-red-500" />
            </button>
          </div>
        {/each}
      {/if}
    </div>
  </div>
  <div class="sitewide-fonts px-2">
    <div class="mb-4 flex items-center justify-between">
      <h1 class="text-2xl font-bold">Popular Fonts</h1>
      <button
        on:click={() => (showFavorites = !showFavorites)}
        title="toggle font favorites"
        class="text-sm font-medium text-[#FF8755]"
      >
        {showFavorites ? 'Show All' : 'Favorites'}
      </button>
    </div>
    {#if $googleFontsStore.loading}
      Loading fonts...
    {:else}
      <div class="grid grid-rows-2 gap-2 text-gray-900">
        {#each getGoogleFonts($metadata.fonts, page) as font}
          <div
            on:click={() => changeFont(font)}
            class="relative block cursor-pointer rounded-md border border-gray-200 bg-white p-2 text-xl hover:bg-gray-100"
            style={computeFamily(font)}
          >
            {font.label}
            <button
              disabled={isUpdatingFavorites}
              class="absolute right-2 z-10"
              on:click|stopPropagation={() => toggleFavorite(font.label)}
            >
              <i
                class="fa-star text-xl text-[#FABE24] {favoritesFonts.includes(font.label) ? 'fa-solid' : 'fa-regular'}"
              />
            </button>
          </div>
        {/each}
      </div>

      <div class="flex w-full space-x-1 py-6 text-gray-900">
        <button class="navbtn" on:click={ev => turnPage(-1, ev)} disabled={isFirstPage}>
          <span class="gg-arrow-left" /> Previous
        </button>
        <button class="navbtn" on:click={ev => turnPage(1, ev)} disabled={isLastPage}>
          Next <span class="gg-arrow-right" />
        </button>
      </div>
    {/if}
  </div>
</div>

<style lang="postcss">
  .navbtn {
    @apply flex w-1/2 cursor-pointer items-center justify-center rounded-lg border border-gray-200 bg-white p-2 text-center shadow-sm hover:bg-gray-300;
  }

  .navbtn:disabled {
    @apply cursor-not-allowed bg-gray-200 opacity-80 hover:bg-gray-200;
  }

  .navbtn > span {
    display: inline-flex;
    --ggs: 0.5;
  }
</style>
